// Dashboard Layout - AgentBheem ERP
// Protected layout - requires authentication
import { Outlet, Navigate, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useMenuStore } from '@/stores/menuStore'
import { useAuthStore } from '@/features/auth/store/authStore'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { Footer } from './Footer'
import { MobileDrawer } from './MobileDrawer'

// Full-screen pages that need no padding and full height (chat, feed, etc.)
// Note: PM workspace (/dashboard/pm) handles its own layout separately
const FULL_SCREEN_ROUTES = [
  '/dashboard/bheem-chat',
  '/dashboard/employee-chat',
  '/dashboard/bheem-feed',
  '/dashboard/company-feed',
  '/dashboard/feed',
]

export function DashboardLayout() {
  const { isCollapsed } = useMenuStore()
  const { isAuthenticated } = useAuthStore()
  const location = useLocation()

  // Check for access token as fallback (for page refresh scenarios)
  const hasToken = Boolean(localStorage.getItem('access_token'))

  // Redirect to login if not authenticated
  if (!isAuthenticated && !hasToken) {
    return <Navigate to="/auth/login" replace state={{ from: location }} />
  }

  // Check if current page is a full-screen page (no padding, full height)
  const isFullScreenPage = FULL_SCREEN_ROUTES.some(route =>
    location.pathname.startsWith(route)
  )

  return (
    <div className="min-h-screen bg-[var(--color-bg-base)]">
      {/* Desktop Sidebar */}
      <Sidebar />

      {/* Mobile Drawer */}
      <MobileDrawer />

      {/* Main Content */}
      <div
        className={cn(
          'flex flex-col transition-all duration-300',
          // Full-screen pages need fixed height for proper h-full cascading
          // Regular pages use min-h-screen so they can scroll naturally
          isFullScreenPage ? 'h-screen' : 'min-h-screen',
          isCollapsed ? 'lg:ml-[72px]' : 'lg:ml-[280px]'
        )}
      >
        {/* Header */}
        <Header />

        {/* Page Content */}
        <main className={cn(
          'flex-1 min-h-0',  // min-h-0 ensures flex child respects overflow
          isFullScreenPage ? 'overflow-hidden' : 'overflow-auto'
        )}>
          <div className={cn(
            isFullScreenPage ? 'h-full overflow-hidden' : 'p-4 lg:p-6'
          )}>
            <Outlet />
          </div>
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  )
}
